import { api } from "@/lib/api-client";
import { useSession } from "@/lib/auth-client";
import { useDashboardRealtime } from "@/lib/useDashboardRealtime";
import { Link } from "wouter";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { signOut } from "@/lib/auth-client";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Bell, User, LogOut, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { ThemeToggle } from "./ThemeToggle";
import { getNotificationPrefs, showBrowserNotification } from "@/lib/notifications";

interface HeaderProps {
  title?: string;
  subtitle?: string;
}

export function Header({ title, subtitle }: HeaderProps) {
  const { data: session } = useSession();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const user = session?.user;

  useEffect(() => {
    loadAlertCount();
  }, []);

  const loadAlertCount = async () => {
    try {
      const { total } = await api.getAlerts({ status: "open", limit: 1 });
      setAlertCount(total);
    } catch (e) {
      console.error("Failed to load alert count", e);
    }
  };

  useDashboardRealtime({
    onLeadMessage: async ({ leadId, role, content }) => {
      const { enabled, onlyWhenHidden } = getNotificationPrefs();
      if (!enabled) return;
      if (onlyWhenHidden && document.visibilityState === "visible") return;
      if (window.location.pathname.startsWith(`/chats/${leadId}`)) return;

      const dir = role === "assistant" ? "Outbound" : role === "user" ? "Inbound" : "New";
      await showBrowserNotification({
        title: `${dir} message`,
        body: content,
        tag: `msg-${leadId}`,
        url: `/chats/${leadId}`,
      });
    },
    onAlertCreated: async ({ alert, lead }) => {
      setAlertCount((c) => c + 1);

      const { enabled, onlyWhenHidden } = getNotificationPrefs();
      if (!enabled) return;
      if (onlyWhenHidden && document.visibilityState === "visible") return;

      const who = lead?.name || lead?.phone || `Lead ${alert.leadId}`;
      const body = [alert.reason, alert.incomingText].filter(Boolean).join("\n\n");
      await showBrowserNotification({
        title: `Needs assistance: ${who}`,
        body,
        tag: `alert-${alert.id}`,
        url: `/alerts`,
      });
    },
    onAlertAcked: () => setAlertCount((c) => Math.max(0, c - 1)),
  });

  useEffect(() => {
    if (title) {
      document.title = `${title} | Eman Time`;
    } else {
      document.title = "Eman Time | WhatsApp AI Agent Dashboard";
    }
  }, [title]);

  const handleSignOut = async () => {
    await signOut();
    window.location.href = "/login";
  };

  const getInitials = (name?: string | null, email?: string | null) => {
    if (name) {
      return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
    }
    if (email && email.length > 0) {
      return email?.[0]?.toUpperCase() || "U";
    }
    return "U";
  };

  return (
    <header className="h-16 border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-30 flex items-center justify-between px-6">
      <div>
        {title && (
          <h1 className="text-xl font-semibold text-foreground">{title}</h1>
        )}
        {subtitle && (
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        )}
      </div>

      <div className="flex items-center gap-2">
        <ThemeToggle />
        <Link href="/alerts">
          <Button variant="ghost" size="icon" className="relative group">
            <Bell className="w-5 h-5 group-hover:text-primary transition-colors" />
            {alertCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-destructive text-[10px] font-bold text-white rounded-full flex items-center justify-center border-2 border-background animate-pulse">
                {alertCount}
              </span>
            )}
          </Button>
        </Link>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2 px-2">
              <Avatar className="w-8 h-8">
                <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                  {getInitials(user?.name, user?.email)}
                </AvatarFallback>
              </Avatar>
              <span className="hidden md:block text-sm font-medium max-w-[150px] truncate">
                {user?.name || user?.email || "User"}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span>{user?.name || "User"}</span>
                <span className="text-xs font-normal text-muted-foreground truncate">
                  {user?.email}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <Link href="/settings">
              <DropdownMenuItem className="cursor-pointer">
                <User className="w-4 h-4 mr-2" />
                Profile
              </DropdownMenuItem>
            </Link>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive cursor-pointer"
              onClick={() => setShowLogoutConfirm(true)}
            >
              <LogOut className="w-4 h-4 mr-2" />
              <span>Sign Out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ConfirmDialog
        open={showLogoutConfirm}
        onOpenChange={setShowLogoutConfirm}
        title="Sign Out"
        description="Are you sure you want to sign out? You will need to log in again to access the dashboard."
        confirmText="Sign Out"
        onConfirm={handleSignOut}
        variant="destructive"
      />
    </header>
  );
}
